import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Paper, Snackbar, Alert, Typography } from '@mui/material';

import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import { IDocumentDirectoryHasDocument } from '../../Interfaces/IDocumentDirectoryHasDocument';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import {
  IGwGFolder,
  IGwGFolderRequirement,
  IGwGFolderSaveObject,
} from '../../Interfaces/IGwGFolder';
import {
  ILegalClientDocumentType,
  isGwGRelevantDocumentType,
} from '../../Interfaces/ILegalClientDocumentType';
import { ILegalTransactionMeeting } from '../../Interfaces/ILegalTransactionMeeting';
import { DocumentDirectoryContent } from './DocumentDirectoryContent';
import { GwGFolderConfigDialog } from './GwG/GwGFolderConfigDialog';
import { GwGFolderContent } from './GwG/GwGFolderContent';
import {
  IGwGFolderState,
  buildFolderView,
  createState,
} from './GwG/gwgFolderState';
import { DocumentDirectoryCreateDialog } from './DocumentDirectoryCreateDialog';
import { DocumentDirectoryTree } from './DocumentDirectoryTree';
import {
  ROOT_NODE_ID,
  getDescendantDirectoryIds,
  getDirectoryById,
  getDirectoryNodeId,
  getDirectoryPath,
  getNextTempId,
  removeDirectoryRecursive,
  reorderDirectory,
} from './documentDirectoryUtils';
import {
  DocumentDirectoryType,
  GWG_DIRECTORY_TYPE,
  isSystemDirectory,
} from './documentDirectoryTypes';
import { ISystemDirectorySource } from './systemDirectoryContent';

interface IProps {
  idLegalTransaction: number;
  documentArray: IDocumentDirectoryHasDocument[];
  documentDirectoryArray: IDocumentDirectory[];
  setDocumentArray: (documentArray: IDocumentDirectoryHasDocument[]) => void;
  setDocumentDirectoryArray: (
    documentDirectoryArray: IDocumentDirectory[]
  ) => void;

  /** Quellen der Systemordner (Maßnahmen, Urkunden, Rechnungen, GwG). */
  systemSource?: ISystemDirectorySource;
  /** Download-Protokoll des Vorgangs — blendet die Spalte „Heruntergeladen" ein. */
  downloadedArray?: IDocumentDownloaded[];
  assignedLegalClients?: ILegalClient[];
  /**
   * Beteiligte juristische Personen des Vorgangs — über **beide** Wege, als
   * Mandant und als Beteiligter eines Rechtsgeschäfts. Wird vom Vorgang
   * berechnet, weil nur der die Rechtsgeschäfte kennt; `assignedLegalClients`
   * allein würde eine als Vertragsgegenstand erfasste Firma übergehen.
   */
  gwgLegalPersonArray?: ILegalClient[];
  /** Unterbindet jede Änderung an der Ablage. */
  isReadOnly?: boolean;

  /**
   * GwG-Ordner: berechnete Sicht je GwG-Ordner des Vorgangs. Ist ein solcher
   * Ordner ausgewählt, tritt seine Arbeitsoberfläche an die Stelle der
   * Ordnerinhalts-Ansicht.
   */
  gwgFolderArray?: IGwGFolder[];
  /**
   * Bereits vorgemerkte Änderungen je GwG-Ordner. Sie liegen im Vorgang, weil
   * `CustomTabPanel` diese Komponente beim Tab-Wechsel ausbaut.
   */
  gwgFolderChangeArray?: IGwGFolderSaveObject[];
  /** Änderungen am GwG-Ordner, die beim Vorgangs-Speichern mitgehen. */
  onGwGFolderChange?: (
    idDocumentDirectory: number,
    folder: IGwGFolder,
    saveObject: IGwGFolderSaveObject
  ) => void;
  /** Termine des Vorgangs — Grundlage terminabhängiger Erinnerungen. */
  meetingArray?: ILegalTransactionMeeting[];
  isCalendarEnabled?: boolean;
  /** Mandanten-Dokumentarten; der GwG-Dialog filtert auf `isGwGRelevant`. */
  legalClientDocumentTypeArray?: ILegalClientDocumentType[];
  /**
   * Zähler, der bei jedem Klick auf die Anlageempfehlung steigt. Steigt er, legt
   * die Datenablage einen GwG-Ordner an und öffnet dessen Konfiguration.
   */
  gwgCreateSignal?: number;
}

/** Element, das gerade per Drag & Drop verschoben wird. */
type IDragItem =
  | { kind: 'directory'; idDocumentDirectory: number }
  | { kind: 'document'; idDocument: number };

/**
 * Interne Datenablage als Dateibrowser: links die Ordnerstruktur als
 * Baumansicht, rechts der Inhalt des ausgewaehlten Ordners.
 */
export const DocumentDirectoryOverview: React.FC<IProps> = props => {
  const isReadOnly = props.isReadOnly === true;

  const [selectedDirectoryId, setSelectedDirectoryId] = useState<number | null>(
    null
  );
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([
    ROOT_NODE_ID,
  ]);
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [createParentId, setCreateParentId] = useState<number | null>(null);

  /** Ordner, dessen GwG-Konfiguration gerade offen ist. */
  const [gwgConfigDirectoryId, setGwgConfigDirectoryId] = useState<
    number | null
  >(null);
  /** Letzter verarbeiteter Stand von `gwgCreateSignal`. */
  const [handledGwgCreateSignal, setHandledGwgCreateSignal] = useState(
    props.gwgCreateSignal ?? 0
  );

  /**
   * Beteiligte juristische Personen — Zeilen der GwG-Ansicht. Der Vorgang
   * liefert sie über beide Wege; fällt das aus, bleibt der bisherige Weg über
   * die Mandanten als Rückfall.
   */
  const gwgLegalPersonArray = useMemo(
    () =>
      props.gwgLegalPersonArray ??
      (props.assignedLegalClients ?? []).filter(
        legalClient => legalClient.idLegalClientType === 2
      ),
    [props.gwgLegalPersonArray, props.assignedLegalClients]
  );

  /** GwG-relevante Dokumentarten für Namen und Reihenfolge. */
  const gwgDocumentTypeArray = useMemo(
    () =>
      (props.legalClientDocumentTypeArray ?? []).filter(
        isGwGRelevantDocumentType
      ),
    [props.legalClientDocumentTypeArray]
  );

  /**
   * Ausgewählter GwG-Ordner, falls es einer ist.
   *
   * Die **Konfiguration** kommt aus dem lokalen Zustand (oder initial aus der
   * Sicht des Backends), die **Anzeige** wird immer frisch aus den Mandanten-
   * Dokumenten des Vorgangs gerechnet. Damit sind die Unterlagen sofort sichtbar
   * — wie in jedem anderen Bereich des Vorgangs — und jede Änderung an
   * Pflicht/Optional oder ein Upload wirkt unmittelbar. Gespeichert wird
   * trotzdem erst mit dem Vorgang.
   */
  const gwgState = useMemo((): IGwGFolderState | null => {
    if (selectedDirectoryId === null) {
      return null;
    }

    const directory = getDirectoryById(
      props.documentDirectoryArray,
      selectedDirectoryId
    );

    if (
      directory === undefined ||
      (directory.DirectoryType ?? 'standard') !== GWG_DIRECTORY_TYPE
    ) {
      return null;
    }

    /**
     * Die vorgemerkten Änderungen liegen im Vorgang, nicht hier: `CustomTabPanel`
     * baut die Datenablage beim Tab-Wechsel aus, lokaler Zustand wäre damit
     * verloren — ein hochgeladenes Dokument verschwände beim Zurückwechseln.
     */
    const pending = (props.gwgFolderChangeArray ?? []).find(
      entry => entry.idDocumentDirectory === selectedDirectoryId
    );
    const backendFolder = (props.gwgFolderArray ?? []).find(
      entry => entry.idDocumentDirectory === selectedDirectoryId
    );
    const saveObject =
      pending ??
      (backendFolder !== undefined
        ? createState(backendFolder).saveObject
        : {
            idDocumentDirectory: selectedDirectoryId,
            RequirementArray: [],
            NewVersionArray: [],
            DeleteVersionIdArray: [],
          });

    return {
      saveObject: saveObject,
      folder: buildFolderView(
        selectedDirectoryId,
        directory.Title,
        saveObject,
        gwgLegalPersonArray,
        gwgDocumentTypeArray
      ),
    };
  }, [
    selectedDirectoryId,
    props.gwgFolderChangeArray,
    props.gwgFolderArray,
    props.documentDirectoryArray,
    gwgLegalPersonArray,
    gwgDocumentTypeArray,
  ]);

  /**
   * Änderung nach oben melden. Der Vorgang hält sie, die Ansicht wird daraus neu
   * gerechnet — kein zweiter Zustand hier, der beim Tab-Wechsel verloren gehen
   * könnte.
   */
  const handleGwGStateChange = (state: IGwGFolderState) => {
    if (props.onGwGFolderChange !== undefined) {
      props.onGwGFolderChange(
        state.saveObject.idDocumentDirectory,
        state.folder,
        state.saveObject
      );
    }
  };
  const [dragItem, setDragItem] = useState<IDragItem | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null | undefined>(
    undefined
  );
  const [message, setMessage] = useState('');

  const documentDirectoryArray = props.documentDirectoryArray;
  const documentArray = props.documentArray;

  const systemSource = useMemo(
    () => props.systemSource ?? {},
    [props.systemSource]
  );

  /** Ausgewaehlter Ordner kann durch Löschen verschwinden -> zurück zur Wurzel. */
  useEffect(() => {
    if (
      selectedDirectoryId !== null &&
      getDirectoryById(documentDirectoryArray, selectedDirectoryId) ===
        undefined
    ) {
      setSelectedDirectoryId(null);
    }
  }, [documentDirectoryArray, selectedDirectoryId]);

  /** Pfad zum ausgewaehlten Ordner immer aufgeklappt halten. */
  useEffect(() => {
    const pathNodeIds = getDirectoryPath(
      documentDirectoryArray,
      selectedDirectoryId
    ).map(documentDirectory =>
      getDirectoryNodeId(documentDirectory.idDocumentDirectory)
    );

    setExpandedNodeIds(previous => {
      const missing = [ROOT_NODE_ID, ...pathNodeIds].filter(
        nodeId => !previous.includes(nodeId)
      );

      return missing.length === 0 ? previous : [...previous, ...missing];
    });
  }, [documentDirectoryArray, selectedDirectoryId]);

  const handleRequestNewDirectory = (
    parent_idDocumentDirectory: number | null
  ) => {
    setCreateParentId(parent_idDocumentDirectory);
    setIsOpenCreate(true);
  };

  const handleCreateDirectory = (
    title: string,
    directoryType: DocumentDirectoryType,
    parent_idDocumentDirectory: number | null
  ) => {
    const newDirectory: IDocumentDirectory = {
      idDocumentDirectory: getNextTempId(
        documentDirectoryArray.map(
          documentDirectory => documentDirectory.idDocumentDirectory
        )
      ),
      idLegalTransaction: props.idLegalTransaction,
      Title: title,
      Parent_idDocumentDirectory: parent_idDocumentDirectory,
      Created_at: null,
      DirectoryType: directoryType,
      Position: documentDirectoryArray.filter(
        entry =>
          (entry.Parent_idDocumentDirectory ?? null) ===
          parent_idDocumentDirectory
      ).length,
    };

    props.setDocumentDirectoryArray([...documentDirectoryArray, newDirectory]);
    setSelectedDirectoryId(newDirectory.idDocumentDirectory);

    /**
     * Ein GwG-Ordner ohne Konfiguration fuehrt keine Dokumentart und bliebe leer.
     * Deshalb direkt nachfragen, welche Unterlagen geprueft werden sollen.
     */
    if (directoryType === GWG_DIRECTORY_TYPE) {
      setGwgConfigDirectoryId(newDirectory.idDocumentDirectory);
    }
  };

  /**
   * Anlageempfehlung angenommen: GwG-Ordner anlegen, sofern es noch keinen gibt.
   * Der Konfigurationsdialog oeffnet sich dabei von selbst, weil
   * `handleCreateDirectory` das fuer die Ordnerart `gwg` uebernimmt. Gibt es den
   * Ordner schon, wird er nur ausgewaehlt.
   */
  useEffect(() => {
    const signal = props.gwgCreateSignal ?? 0;

    if (signal === handledGwgCreateSignal) {
      return;
    }

    setHandledGwgCreateSignal(signal);

    const existing = documentDirectoryArray.find(
      directory => directory.DirectoryType === GWG_DIRECTORY_TYPE
    );

    if (existing !== undefined) {
      setSelectedDirectoryId(existing.idDocumentDirectory);

      return;
    }

    handleCreateDirectory('GwG', GWG_DIRECTORY_TYPE, null);
    // Absichtlich nur am Signal haengend: der Effekt soll ausschliesslich auf
    // einen Klick der Anlageempfehlung reagieren, nicht auf jede Aenderung der
    // Ordnerliste.
  }, [props.gwgCreateSignal]);

  /**
   * Konfiguration eines GwG-Ordners uebernehmen. Beim Anlegen entsteht dabei der
   * lokale Zustand des Ordners, damit die Ansicht sofort die gewaehlten
   * Dokumentarten zeigt — noch bevor das Backend die Sicht neu berechnet hat.
   */
  const handleGwGConfigSave = (
    idDocumentDirectory: number,
    requirementArray: IGwGFolderRequirement[]
  ) => {
    const pending = (props.gwgFolderChangeArray ?? []).find(
      entry => entry.idDocumentDirectory === idDocumentDirectory
    );
    const backendFolder = (props.gwgFolderArray ?? []).find(
      entry => entry.idDocumentDirectory === idDocumentDirectory
    );
    const baseSaveObject =
      pending ??
      (backendFolder !== undefined
        ? createState(backendFolder).saveObject
        : {
            idDocumentDirectory: idDocumentDirectory,
            RequirementArray: [],
            NewVersionArray: [],
            DeleteVersionIdArray: [],
          });
    const saveObject = {
      ...baseSaveObject,
      idDocumentDirectory: idDocumentDirectory,
      RequirementArray: requirementArray,
    };

    handleGwGStateChange({
      saveObject: saveObject,
      folder: buildFolderView(
        idDocumentDirectory,
        getDirectoryById(documentDirectoryArray, idDocumentDirectory)?.Title,
        saveObject,
        gwgLegalPersonArray,
        gwgDocumentTypeArray
      ),
    });
    setMessage(
      'Die GwG-Konfiguration wird mit dem Speichern des Vorgangs übernommen.'
    );
  };

  /**
   * Ordner samt Unterordnern entfernen. `moveDocumentsTo` bestimmt, ob die darin
   * abgelegten Dateien mitgeloescht oder in einen anderen Ordner verschoben
   * werden.
   */
  const handleDeleteDirectory = (
    idDocumentDirectory: number,
    moveDocumentsTo?: number
  ) => {
    const [nextDirectoryArray, nextDocumentArray] = removeDirectoryRecursive(
      idDocumentDirectory,
      documentDirectoryArray,
      documentArray,
      moveDocumentsTo
    );

    props.setDocumentDirectoryArray(nextDirectoryArray);
    props.setDocumentArray(nextDocumentArray);

    if (
      selectedDirectoryId === idDocumentDirectory ||
      (selectedDirectoryId !== null &&
        getDescendantDirectoryIds(
          documentDirectoryArray,
          idDocumentDirectory
        ).includes(selectedDirectoryId))
    ) {
      setSelectedDirectoryId(
        getDirectoryById(documentDirectoryArray, idDocumentDirectory)
          ?.Parent_idDocumentDirectory ?? null
      );
    }
  };

  /**
   * Ordner innerhalb seiner Ebene umsortieren. Nur sinnvoll, wenn beide Ordner
   * denselben Elternordner haben — sonst ist es ein Verschieben.
   */
  const handleReorderDirectory = (
    idTargetDocumentDirectory: number,
    placeAfter: boolean
  ) => {
    const currentDragItem = dragItem;
    setDragItem(null);
    setDropTargetId(undefined);

    if (
      currentDragItem === null ||
      currentDragItem.kind !== 'directory' ||
      isReadOnly
    ) {
      return;
    }

    const moved = getDirectoryById(
      documentDirectoryArray,
      currentDragItem.idDocumentDirectory
    );
    const target = getDirectoryById(
      documentDirectoryArray,
      idTargetDocumentDirectory
    );

    if (moved === undefined || target === undefined) {
      return;
    }

    /// Andere Ebene: erst in den Elternordner des Ziels verschieben, dann dort
    /// einsortieren.
    if (
      (moved.Parent_idDocumentDirectory ?? null) !==
      (target.Parent_idDocumentDirectory ?? null)
    ) {
      if (
        getDescendantDirectoryIds(
          documentDirectoryArray,
          moved.idDocumentDirectory
        ).includes(target.idDocumentDirectory)
      ) {
        setMessage(
          'Ein Ordner kann nicht in einen seiner eigenen Unterordner verschoben werden.'
        );
        return;
      }

      const movedArray = documentDirectoryArray.map(documentDirectory =>
        documentDirectory.idDocumentDirectory === moved.idDocumentDirectory
          ? {
              ...documentDirectory,
              Parent_idDocumentDirectory:
                target.Parent_idDocumentDirectory ?? null,
            }
          : documentDirectory
      );

      props.setDocumentDirectoryArray(
        reorderDirectory(
          moved.idDocumentDirectory,
          idTargetDocumentDirectory,
          placeAfter,
          movedArray
        )
      );
      return;
    }

    props.setDocumentDirectoryArray(
      reorderDirectory(
        moved.idDocumentDirectory,
        idTargetDocumentDirectory,
        placeAfter,
        documentDirectoryArray
      )
    );
  };

  const handleDragOverDirectory = (idDocumentDirectory: number | null) => {
    if (dragItem !== null) {
      setDropTargetId(idDocumentDirectory);
    }
  };

  const handleDragLeaveDirectory = () => {
    setDropTargetId(undefined);
  };

  const handleDropOnDirectory = (idDocumentDirectory: number | null) => {
    const currentDragItem = dragItem;
    setDragItem(null);
    setDropTargetId(undefined);

    if (currentDragItem === null || isReadOnly) {
      return;
    }

    const targetDirectory = getDirectoryById(
      documentDirectoryArray,
      idDocumentDirectory
    );

    if (targetDirectory !== undefined && isSystemDirectory(targetDirectory)) {
      setMessage(
        'Systemordner spiegeln ihren Inhalt automatisch — dort kann nichts abgelegt werden.'
      );
      return;
    }

    if (currentDragItem.kind === 'document') {
      if (idDocumentDirectory === null) {
        setMessage(
          'Dateien können nur innerhalb eines Ordners abgelegt werden.'
        );
        return;
      }

      props.setDocumentArray(
        documentArray.map(documentObject =>
          documentObject.idDocument === currentDragItem.idDocument
            ? { ...documentObject, idDocumentDirectory: idDocumentDirectory }
            : documentObject
        )
      );
      return;
    }

    const movedId = currentDragItem.idDocumentDirectory;

    if (movedId === idDocumentDirectory) {
      return;
    }

    if (
      idDocumentDirectory !== null &&
      getDescendantDirectoryIds(documentDirectoryArray, movedId).includes(
        idDocumentDirectory
      )
    ) {
      setMessage(
        'Ein Ordner kann nicht in einen seiner eigenen Unterordner verschoben werden.'
      );
      return;
    }

    props.setDocumentDirectoryArray(
      documentDirectoryArray.map(documentDirectory =>
        documentDirectory.idDocumentDirectory === movedId
          ? {
              ...documentDirectory,
              Parent_idDocumentDirectory: idDocumentDirectory,
            }
          : documentDirectory
      )
    );
  };

  const createParentTitle =
    getDirectoryById(documentDirectoryArray, createParentId)?.Title ??
    'Interne Datenablage';

  return (
    <>
      <DocumentDirectoryCreateDialog
        isOpen={isOpenCreate}
        setIsOpen={setIsOpenCreate}
        parent_idDocumentDirectory={createParentId}
        parentTitle={createParentTitle}
        documentDirectoryArray={documentDirectoryArray}
        onCreate={handleCreateDirectory}
      />

      <GwGFolderConfigDialog
        isOpen={gwgConfigDirectoryId !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setGwgConfigDirectoryId(null);
          }
        }}
        documentTypeArray={props.legalClientDocumentTypeArray ?? []}
        initialRequirementArray={
          gwgConfigDirectoryId === null
            ? undefined
            : ((props.gwgFolderChangeArray ?? []).find(
                entry => entry.idDocumentDirectory === gwgConfigDirectoryId
              )?.RequirementArray ??
              (props.gwgFolderArray ?? [])
                .filter(
                  entry => entry.idDocumentDirectory === gwgConfigDirectoryId
                )
                .flatMap(
                  entry => createState(entry).saveObject.RequirementArray
                ))
        }
        meetingArray={props.meetingArray ?? []}
        isCalendarEnabled={props.isCalendarEnabled === true}
        legalPersonCount={gwgLegalPersonArray.length}
        onSave={(requirementArray: IGwGFolderRequirement[]) => {
          if (gwgConfigDirectoryId !== null) {
            handleGwGConfigSave(gwgConfigDirectoryId, requirementArray);
          }

          setGwgConfigDirectoryId(null);
        }}
      />

      <Snackbar
        open={message !== ''}
        autoHideDuration={4000}
        onClose={() => setMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity='info' onClose={() => setMessage('')}>
          {message}
        </Alert>
      </Snackbar>

      <Typography variant='h5' sx={{ mb: 2 }}>
        Interne Datenablage
      </Typography>

      <Grid container spacing={2} alignItems='stretch'>
        <Grid item xs={12} md={4} lg={3}>
          <Paper
            variant='outlined'
            sx={{ p: 1, height: '100%', minHeight: 320, overflow: 'auto' }}
            onDragEnd={() => {
              setDragItem(null);
              setDropTargetId(undefined);
            }}
          >
            <DocumentDirectoryTree
              documentDirectoryArray={documentDirectoryArray}
              documentArray={documentArray}
              systemSource={systemSource}
              selectedDirectoryId={selectedDirectoryId}
              setSelectedDirectoryId={setSelectedDirectoryId}
              expandedNodeIds={expandedNodeIds}
              setExpandedNodeIds={setExpandedNodeIds}
              dropTargetId={dropTargetId}
              onDragOverDirectory={handleDragOverDirectory}
              onDragLeaveDirectory={handleDragLeaveDirectory}
              onDropOnDirectory={handleDropOnDirectory}
              onDragStartDirectory={(idDocumentDirectory: number) =>
                setDragItem({
                  kind: 'directory',
                  idDocumentDirectory: idDocumentDirectory,
                })
              }
              onReorderDirectory={handleReorderDirectory}
              isReadOnly={isReadOnly}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          {/* Der GwG-Ordner bekommt eine eigene Arbeitsoberflaeche: er spiegelt
              keine Dateien des Vorgangs, sondern zeigt die Unterlagen der
              beteiligten juristischen Personen samt Anforderung, Erinnerungen
              und Vollstaendigkeit. */}
          {gwgState !== null ? (
            <GwGFolderContent
              state={gwgState}
              setState={handleGwGStateChange}
              meetingArray={props.meetingArray ?? []}
              isCalendarEnabled={props.isCalendarEnabled === true}
              isReadOnly={isReadOnly}
              legalPersonArray={gwgLegalPersonArray}
              onOpenConfig={() =>
                setGwgConfigDirectoryId(gwgState.folder.idDocumentDirectory)
              }
            />
          ) : (
            <DocumentDirectoryContent
              documentDirectoryArray={documentDirectoryArray}
              setDocumentDirectoryArray={props.setDocumentDirectoryArray}
              documentArray={documentArray}
              setDocumentArray={props.setDocumentArray}
              selectedDirectoryId={selectedDirectoryId}
              setSelectedDirectoryId={setSelectedDirectoryId}
              systemSource={systemSource}
              downloadedArray={props.downloadedArray}
              assignedLegalClients={props.assignedLegalClients}
              onRequestNewDirectory={handleRequestNewDirectory}
              onDeleteDirectory={handleDeleteDirectory}
              dropTargetId={dropTargetId}
              onDragOverDirectory={handleDragOverDirectory}
              onDragLeaveDirectory={handleDragLeaveDirectory}
              onDropOnDirectory={handleDropOnDirectory}
              onDragStartDirectory={(idDocumentDirectory: number) =>
                setDragItem({
                  kind: 'directory',
                  idDocumentDirectory: idDocumentDirectory,
                })
              }
              onDragStartDocument={(idDocument: number) =>
                setDragItem({ kind: 'document', idDocument: idDocument })
              }
              isReadOnly={isReadOnly}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};
